import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { storage } from '../../lib/firebase';

const ALLOWED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
]);

const MAX_IMAGE_SIZE_BYTES = 12 * 1024 * 1024;

function normalizeSegment(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function extensionFromFile(file: File) {
  const explicit = file.name.split('.').pop()?.trim().toLowerCase() ?? '';
  if (explicit) return explicit;

  switch (file.type) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    case 'image/avif':
      return 'avif';
    default:
      return '';
  }
}

function validatePublicationImage(file: File) {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error('Publication images must be JPG, PNG, WEBP, GIF, or AVIF.');
  }

  if (file.size <= 0) {
    throw new Error('Image file is empty.');
  }

  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    throw new Error('Publication images must be 12MB or smaller.');
  }

  const ext = extensionFromFile(file);
  if (!ext) {
    throw new Error('Image file type is not supported.');
  }

  return ext;
}

export async function uploadPublicationImage(params: {
  file: File;
  publicationSlug?: string;
  userId?: string | null;
  slot?: string;
}) {
  const ext = validatePublicationImage(params.file);
  const safeSlug = normalizeSegment(params.publicationSlug || 'draft');
  const safeUser = normalizeSegment(params.userId || 'staff');
  const safeSlot = normalizeSegment(params.slot || 'image');
  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const path = `publications/${safeSlug}/${safeUser}/${safeSlot}-${stamp}.${ext}`;
  const target = storageRef(storage, path);

  await uploadBytes(target, params.file, {
    contentType: params.file.type,
    cacheControl: 'public,max-age=31536000,immutable',
    customMetadata: {
      publicationSlug: safeSlug,
      uploadedBy: safeUser,
      slot: safeSlot,
    },
  });

  const downloadUrl = await getDownloadURL(target);

  return {
    downloadUrl,
    storagePath: path,
  };
}
